import { useMemo, useState } from 'react'

import { Divider, Loader, TextInput } from '@mantine/core'

import { useMutation, useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { useApplicantDrawerContext } from 'src/context/ApplicantDrawerContext'
import {
  DELETE_APPLICANT_CUSTOM_FIELD_VALUE,
  GET_APPLICANT_CUSTOM_FIELD_VALUES,
  GET_CUSTOM_FIELDS,
  SET_APPLICANT_CUSTOM_FIELD_VALUE,
} from 'src/graphql/customFields.graphql'

import CustomFieldRow from './CustomFieldRow'
import { buildInputForFieldType, getValueForFieldType } from './customFieldsUtils'
import ValueChangeHistoryDrawer from './ValueChangeHistoryDrawer'

// IMPORTANT: Page limit must match MAX_CUSTOM_FIELDS_PER_COMPANY (100) to avoid
// truncating custom field values. This constant is used in both the initial query
// and refetchQueries to ensure cache consistency.
const CUSTOM_FIELDS_PAGE_INFO = { limit: 100, offset: 0 }

const CustomFieldsTab = ({ applicant }) => {
  const drawerContext = useApplicantDrawerContext()
  const [historyDrawerOpened, setHistoryDrawerOpened] = useState(false)
  const [selectedValueForHistory, setSelectedValueForHistory] = useState(null)
  const [savingFieldId, setSavingFieldId] = useState(null)
  const [searchText, setSearchText] = useState('')

  // Fetch all custom fields for company (active fields - load all 100 at once)
  const { data: customFieldsData, loading: loadingCustomFields } = useQuery(
    GET_CUSTOM_FIELDS,
    {
      variables: {
        status: 'ACTIVE',
        pageInfo: CUSTOM_FIELDS_PAGE_INFO,
      },
    }
  )

  // Fetch current values for this applicant
  const { data: valuesData, loading: loadingValues } = useQuery(
    GET_APPLICANT_CUSTOM_FIELD_VALUES,
    {
      variables: {
        applicantId: applicant.applicantId,
        pageInfo: CUSTOM_FIELDS_PAGE_INFO,
      },
    }
  )

  // Both queries must complete before rendering content
  const loading = loadingCustomFields || loadingValues

  const [setCustomFieldValue, { loading: savingSet }] = useMutation(
    SET_APPLICANT_CUSTOM_FIELD_VALUE,
    {
      refetchQueries: [
        {
          query: GET_APPLICANT_CUSTOM_FIELD_VALUES,
          variables: {
            applicantId: applicant.applicantId,
            pageInfo: CUSTOM_FIELDS_PAGE_INFO,
          },
        },
      ],
      onCompleted: () => {
        drawerContext?.triggerRefresh()
      },
      onError: (error) => {
        console.error('Error updating custom field:', error)
        toast('Error updating custom fields', 'error')
      },
    }
  )

  const [deleteCustomFieldValue, { loading: savingDelete }] = useMutation(
    DELETE_APPLICANT_CUSTOM_FIELD_VALUE,
    {
      refetchQueries: [
        {
          query: GET_APPLICANT_CUSTOM_FIELD_VALUES,
          variables: {
            applicantId: applicant.applicantId,
            pageInfo: CUSTOM_FIELDS_PAGE_INFO,
          },
        },
      ],
      onCompleted: () => {
        drawerContext?.triggerRefresh()
      },
      onError: (error) => {
        console.error('Error deleting custom field:', error)
        toast('Error deleting custom field', 'error')
      },
    }
  )

  const saving = savingSet || savingDelete

  // Active fields come from query (already filtered by status)
  const activeFields = useMemo(
    () =>
      [...(customFieldsData?.customFields?.items || [])].sort(
        (a, b) => a.sortOrder - b.sortOrder
      ),
    [customFieldsData]
  )

  // Archived fields are extracted from values that have archived customFields
  const archivedFields = useMemo(() => {
    const archivedWithValues = []
    // Extract archived fields from valuesData (includes customField relation with status)
    valuesData?.applicantCustomFieldValues?.items?.forEach((value) => {
      if (value.customField && value.customField.status === 'ARCHIVED') {
        archivedWithValues.push(value.customField)
      }
    })
    // Sort by sortOrder
    return archivedWithValues.sort((a, b) => a.sortOrder - b.sortOrder)
  }, [valuesData])

  // Create map of fieldId -> value for easy lookup
  const valueMap = useMemo(() => {
    const map = {}
    valuesData?.applicantCustomFieldValues?.items?.forEach((v) => {
      map[v.customFieldId] = v
    })
    return map
  }, [valuesData])

  // Filter fields by search text
  const filteredActiveFields = useMemo(() => {
    if (!searchText.trim()) return activeFields
    return activeFields.filter((field) =>
      field.label.toLowerCase().includes(searchText.toLowerCase())
    )
  }, [activeFields, searchText])

  const filteredArchivedFields = useMemo(() => {
    const fieldsWithValues = archivedFields.filter((field) => valueMap[field.customFieldId])
    if (!searchText.trim()) return fieldsWithValues
    return fieldsWithValues.filter((field) =>
      field.label.toLowerCase().includes(searchText.toLowerCase())
    )
  }, [archivedFields, valueMap, searchText])

  // Show search bar if there are more than 10 total fields
  const showSearchBar = activeFields.length > 10

  const handleFieldSave = async (field, newValue) => {
    try {
      setSavingFieldId(field.customFieldId)

      // Validate required fields
      if (field.isRequired) {
        if (
          newValue === null ||
          newValue === undefined ||
          newValue === '' ||
          (Array.isArray(newValue) && newValue.length === 0)
        ) {
          toast(`${field.label} is required`, 'error')
          setSavingFieldId(null)
          return
        }
      }

      const existingValue = valueMap[field.customFieldId]
      const hadValue = existingValue != null

      // Determine if value is "cleared" (user explicitly set to empty/null)
      const isCleared =
        newValue !== undefined &&
        (newValue === null ||
          newValue === '' ||
          ((field.fieldType === 'SINGLE_SELECT' ||
            field.fieldType === 'MULTI_SELECT') &&
            Array.isArray(newValue) &&
            newValue.length === 0))

      if (isCleared && hadValue) {
        // Delete existing value
        await deleteCustomFieldValue({
          variables: {
            applicantCustomFieldValueId:
              existingValue.applicantCustomFieldValueId,
          },
        })
      } else if (isCleared && !hadValue) {
        // Don't save if trying to clear an already empty field
        setSavingFieldId(null)
        return
      } else if (newValue !== undefined && !isCleared) {
        // Save or update value (only if not cleared)
        const input = buildInputForFieldType(
          field.fieldType,
          applicant.applicantId,
          field.customFieldId,
          newValue
        )

        await setCustomFieldValue({ variables: { input } })
      }

      toast('Custom field updated successfully', 'success')
      setSavingFieldId(null)
    } catch (error) {
      console.error('Error saving custom field:', error)
      setSavingFieldId(null)
      // Toast already shown in mutation's onError
    }
  }

  const handleShowHistory = (value, field) => {
    setSelectedValueForHistory({
      applicantCustomFieldValueId: value.applicantCustomFieldValueId,
      fieldLabel: field.label,
    })
    setHistoryDrawerOpened(true)
  }

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-hidden">
      {loading ? (
        <div className="flex flex-1 flex-col items-center justify-center">
          <Loader size="xs" color="gray" />
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-3 overflow-hidden">
          {/* Search Bar - shown when more than 10 fields */}
          {showSearchBar && (
            <div className="flex-shrink-0">
              <TextInput
                placeholder="Search custom fields..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                size="xs"
                className="w-full"
              />
            </div>
          )}

          <div className="flex flex-1 flex-col gap-3 overflow-auto">
            {/* Active Fields (Editable) */}
            {filteredActiveFields.length > 0 && (
              <div className="flex flex-col gap-2">
                {filteredActiveFields.map((field) => (
                  <CustomFieldRow
                    key={`${applicant.applicantId}-${field.customFieldId}`}
                    field={field}
                    value={valueMap[field.customFieldId]}
                    onSave={handleFieldSave}
                    saving={savingFieldId === field.customFieldId}
                    onShowHistory={handleShowHistory}
                  />
                ))}
              </div>
            )}

            {/* Archived Fields (Read-only) */}
            {filteredArchivedFields.length > 0 && (
              <>
                <Divider
                  label="Archived Fields"
                  labelPosition="center"
                  className="text-xs text-gray-500"
                />
                <div className="flex flex-col gap-2 opacity-60">
                  {filteredArchivedFields.map((field) => (
                    <CustomFieldRow
                      key={`${applicant.applicantId}-${field.customFieldId}`}
                      field={field}
                      value={valueMap[field.customFieldId]}
                      editing={false}
                      readOnly={true}
                      onShowHistory={handleShowHistory}
                    />
                  ))}
                </div>
              </>
            )}

            {/* No results states */}
            {searchText.trim() && filteredActiveFields.length === 0 && filteredArchivedFields.length === 0 && (
              <div className="flex flex-1 flex-col items-center justify-center text-xs text-doubleNickel-gray-500">
                No matching custom fields found
              </div>
            )}

            {!searchText.trim() && activeFields.length === 0 &&
              archivedFields.filter((field) => valueMap[field.customFieldId]).length === 0 && (
                <div className="flex flex-1 flex-col items-center justify-center text-xs text-doubleNickel-gray-500">
                  No custom fields configured
                </div>
              )}
          </div>
        </div>
      )}

      <ValueChangeHistoryDrawer
        opened={historyDrawerOpened}
        onClose={() => setHistoryDrawerOpened(false)}
        applicantCustomFieldValueId={
          selectedValueForHistory?.applicantCustomFieldValueId
        }
        fieldLabel={selectedValueForHistory?.fieldLabel}
      />
    </div>
  )
}

export default CustomFieldsTab
